import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Form, Row, Col, Badge, Spinner, Alert, InputGroup, Modal, Card } from 'react-bootstrap';
import { FaArrowLeft, FaDownload, FaSearch, FaEye, FaUser, FaInbox } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../api/apiClient';
import { toast } from 'react-toastify';

const normalizeAnswerValue = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }

    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed;
        }
        return parsed;
      } catch (error) {
        return trimmed;
      }
    }

    if (trimmed.includes('\n')) {
      return trimmed.split('\n').map((item) => item.trim()).filter(Boolean);
    }

    return trimmed;
  }

  if (value === null || value === undefined) {
    return '';
  }

  return value;
};

const renderAnswerContent = (value, questionType) => {
  const normalized = normalizeAnswerValue(value);

  if (Array.isArray(normalized)) {
    return (
      <ul className="mb-0 ps-3">
        {normalized.map((item, idx) => (
          <li key={idx} className="text-muted small">
            {item}
          </li>
        ))}
      </ul>
    );
  }

  if (!normalized) {
    return <span className="text-muted">No response</span>;
  }

  if (questionType === 'textarea') {
    return <div style={{ whiteSpace: 'pre-wrap' }}>{normalized}</div>;
  }

  return <span className="fw-semibold">{normalized}</span>;
};

const OrgSurveyResponses = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();

  const [survey, setSurvey] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);

  useEffect(() => {
    fetchResponses();
  }, [id]);

  const fetchResponses = async () => {
    try {
      setLoading(true);
      const response = await api.surveys.getDetailedResponses(id);
      
      // Handle different response structures
      if (response.data?.success) {
        setSurvey(response.data.data?.survey || response.data.survey || null);
        setResponses(response.data.data?.responses || response.data.data?.data || response.data.responses || response.data.data || []);
      } else {
        // If no success flag, assume direct data structure
        setSurvey(response.data?.survey || null);
        setResponses(response.data?.responses || response.data?.data || []);
      }
    } catch (error) {
      console.error('Error fetching survey responses:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load responses';
      toast.error(errorMessage);
      // Don't navigate away immediately, let user see the error state
      setSurvey(null);
      setResponses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleViewDetails = (response) => {
    setSelectedResponse(response);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedResponse(null);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await api.surveys.exportResponses(id);

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `survey_${id}_responses.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Responses exported successfully');
    } catch (error) {
      console.error('Error exporting survey responses:', error);
      toast.error('Failed to export responses');
    } finally {
      setExporting(false);
    }
  };

  const filteredResponses = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return (responses || []).filter((response) => {
      const userName = response.user?.name || '';
      const userEmail = response.user?.email || '';
      return (
        userName.toLowerCase().includes(search) ||
        userEmail.toLowerCase().includes(search)
      );
    });
  }, [responses, searchTerm]);

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="d-flex align-items-center gap-3 mb-4">
        <Button
          bsPrefix="custom-hamburger"
          onClick={() => navigate('/organization/surveys')}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium custom-hamburger custom-hamburger-primary"
          title="Back to Surveys"
        >
          <FaArrowLeft className="w-5 h-5" /> Back
        </Button>
        <div>
          <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
            Survey Responses
          </h5>
          {survey && (
            <p className="text-sm text-gray-600 mt-1 mb-0">{survey.title}</p>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center" style={{ height: '400px' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
        </div>
      ) : !survey ? (
        <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
          <Card.Body className="p-5 text-center">
            <FaInbox className="text-gray-300 mb-3" style={{ fontSize: '3rem' }} />
            <h5 className="text-gray-600 mb-2">Survey not found</h5>
            <p className="text-muted text-sm mb-3">The survey you're looking for doesn't exist or has been removed.</p>
            <Button
              bsPrefix="custom-hamburger"
              onClick={() => navigate('/organization/surveys')}
              className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 px-4 py-2"
            >
              Back to Surveys
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <>

      <Row className="mb-4 g-2 sm:g-3">
        <Col xs={12} md={8} className="mb-2 mb-md-0">
          <div className="flex shadow-sm rounded-lg sm:rounded-xl overflow-hidden bg-white">
            <div className="flex items-center px-3 bg-white border-r border-gray-200">
              <FaSearch className="w-4 h-4 text-purple-600" />
            </div>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full px-3 py-2 border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base outline-none"
            />
          </div>
        </Col>
        <Col xs={12} md={4}>
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleExport}
            disabled={exporting}
            className="w-full border-1 border-green-600 bg-white text-green-600 hover:from-green-700 hover:to-emerald-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
          >
            <FaDownload className="w-4 h-4" />
            <span className="whitespace-nowrap">{exporting ? 'Exporting...' : 'Export CSV'}</span>
          </Button>
        </Col>
      </Row>

      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <Card.Body className="p-0">
          {(filteredResponses || []).length === 0 ? (
            <div className="text-center py-10 bg-white">
              <FaInbox className="text-gray-300 mb-3" style={{ fontSize: '3rem' }} />
              <h5 className="text-gray-600 mb-2">No responses found</h5>
              <p className="text-muted text-sm">No responses found yet.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <tr>
                    <th className="py-2 px-3">#</th>
                    <th className="py-2 px-3">User</th>
                    <th className="py-2 px-3">Email</th>
                    <th className="py-2 px-3">Submitted At</th>
                    <th className="py-2 px-3">Answers</th>
                    <th className="py-2 px-3 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResponses.map((response, index) => (
                    <tr key={response.response_id || response.id || `response-${index}`} className="border-b border-purple-50 hover:bg-purple-50/60 transition-all">
                      <td className="py-2 px-3 align-middle">
                        <Badge bg="secondary">{index + 1}</Badge>
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <div className="d-flex align-items-center gap-2">
                          <FaUser className="text-purple-600" />
                          <span className="fw-semibold text-gray-800 text-sm">{response.user?.name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="py-2 px-3 align-middle text-gray-600 text-sm">
                        {response.user?.email || '—'}
                      </td>
                      <td className="py-2 px-3 align-middle text-gray-600 text-xs">
                        {response.submitted_at ? new Date(response.submitted_at).toLocaleString() : '—'}
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <Badge bg="primary">{response.answers?.length || 0}</Badge>
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <div className="d-flex justify-content-end">
                          <button
                            onClick={() => handleViewDetails(response)}
                            className="text-purple-600 hover:bg-purple-100 p-2 rounded-lg transition-all"
                            title="View details"
                            type="button"
                          >
                            <FaEye className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showDetailModal} onHide={handleCloseDetailModal} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">Response Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-3 sm:p-5">
          {selectedResponse && (
            <>
              <div className="mb-4 p-3 bg-white rounded-lg shadow-sm">
                <h6 className="text-purple-700 font-semibold mb-2">Respondent</h6>
                <div className="fw-semibold text-gray-800">{selectedResponse.user?.name || 'Unknown'}</div>
                <div className="text-muted text-sm">{selectedResponse.user?.email || '—'}</div>
                <div className="text-muted text-xs mt-1">
                  Submitted: {selectedResponse.submitted_at ? new Date(selectedResponse.submitted_at).toLocaleString() : '—'}
                </div>
              </div>

              <h6 className="text-purple-700 font-semibold mb-3">Answers</h6>
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <Table bordered hover className="mb-0">
                  <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                    <tr>
                      <th className="py-2 px-3" style={{ width: '45%' }}>Question</th>
                      <th className="py-2 px-3">Answer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedResponse.answers || []).map((answer, idx) => (
                      <tr key={idx} className="border-b border-purple-50">
                        <td className="align-middle py-2 px-3">
                          <div className="d-flex align-items-start gap-2">
                            <Badge bg="secondary">{idx + 1}</Badge>
                            <span className="text-gray-800 text-sm">{answer.question_text}</span>
                          </div>
                        </td>
                        <td className="align-middle py-2 px-3">
                          {renderAnswerContent(answer.answer, answer.question_type)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleCloseDetailModal}
            className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-sm px-4 py-2"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
        </>
      )}
    </div>
  );
};

export default OrgSurveyResponses;
